// `createMorphHelpers` is serialized with `toString()`, so it must reference nothing from module
// scope — a closure binding type-checks here and is undefined in the composition.

export const MORPH_HELPER_SCRIPT_ID = 'l5-gsap-morph';

interface MorphViewBox {
    x: number;
    y: number;
    w: number;
    h: number;
}

interface MorphPath {
    d: string;
    fill: string;
    el: SVGPathElement;
}

interface MorphPair {
    a: MorphPath | null;
    b: MorphPath | null;
}

export interface MorphOptions {
    from: string | Element;
    to: string | Element;
    stage: string | Element;
    at?: number | string;
    duration?: number;
    ease?: string;
    fill?: string;
    toFill?: string;
}

export interface MorphTimeline {
    to(target: unknown, vars: Record<string, unknown>, position?: number | string): unknown;
}

export function createMorphHelpers(): {
    morph(tl: MorphTimeline, options: MorphOptions): SVGPathElement[];
} {
    const NS = 'http://www.w3.org/2000/svg';
    const FALLBACK_PATH = 'M-0.5,-0.5 L0.5,-0.5 L0.5,0.5 L-0.5,0.5 Z';

    function gsapGlobal(): { set(target: unknown, vars: Record<string, unknown>): unknown } {
        return (window as unknown as Record<string, never>)['gsap'];
    }

    function parseViewBox(str: string | null | undefined): MorphViewBox {
        if (!str) {
            return { x: 0, y: 0, w: 100, h: 100 };
        }
        const n = str
            .trim()
            .split(/[\s,]+/)
            .map(Number);
        return { x: n[0] || 0, y: n[1] || 0, w: n[2] || 100, h: n[3] || 100 };
    }

    function pathFill(el: SVGPathElement): string {
        const attr = el.getAttribute('fill');
        if (attr === 'none') {
            return 'rgba(0,0,0,0)';
        }
        if (attr && attr.indexOf('url(') !== 0) {
            return attr;
        }
        const computed = getComputedStyle(el).fill;
        return computed && computed !== 'none' ? computed : '#ffffff';
    }

    function getPaths(root: Element): MorphPath[] {
        const plugin = (window as unknown as Record<string, never>)['MorphSVGPlugin'] as
            | { convertToPath(targets: Element[]): unknown }
            | undefined;
        if (plugin && plugin.convertToPath) {
            const shapes = root.querySelectorAll('circle,rect,ellipse,line,polygon,polyline');
            if (shapes.length) {
                plugin.convertToPath(Array.prototype.slice.call(shapes) as Element[]);
            }
        }
        const paths = Array.prototype.slice.call(root.querySelectorAll('path')) as SVGPathElement[];
        return paths.map(function (el) {
            return { d: el.getAttribute('d') || FALLBACK_PATH, fill: pathFill(el), el: el };
        });
    }

    function centerDotPath(path: MorphPath, pathVb: MorphViewBox, renderVb: MorphViewBox): string {
        try {
            const bb = path.el.getBBox();
            const nx = (bb.x + bb.width / 2 - pathVb.x) / pathVb.w;
            const ny = (bb.y + bb.height / 2 - pathVb.y) / pathVb.h;
            const cx = renderVb.x + nx * renderVb.w;
            const cy = renderVb.y + ny * renderVb.h;
            const r = 0.5;
            return (
                'M' +
                (cx - r) +
                ',' +
                (cy - r) +
                ' L' +
                (cx + r) +
                ',' +
                (cy - r) +
                ' L' +
                (cx + r) +
                ',' +
                (cy + r) +
                ' L' +
                (cx - r) +
                ',' +
                (cy + r) +
                ' Z'
            );
        } catch (e) {
            return FALLBACK_PATH;
        }
    }

    // Hungarian assignment over centre distance and relative area.
    function pairPaths(
        pathsA: MorphPath[],
        pathsB: MorphPath[],
        vbA: MorphViewBox,
        vbB: MorphViewBox,
    ): MorphPair[] {
        function feat(p: MorphPath, vb: MorphViewBox): { cx: number; cy: number; sr: number } {
            try {
                const bb = p.el.getBBox();
                return {
                    cx: (bb.x + bb.width / 2 - vb.x) / vb.w,
                    cy: (bb.y + bb.height / 2 - vb.y) / vb.h,
                    sr: Math.sqrt((bb.width * bb.height) / (vb.w * vb.h)),
                };
            } catch (e) {
                return { cx: 0.5, cy: 0.5, sr: 0 };
            }
        }

        const fA = pathsA.map(function (p) {
            return feat(p, vbA);
        });
        const fB = pathsB.map(function (p) {
            return feat(p, vbB);
        });
        const n = pathsA.length;
        const m = pathsB.length;
        const size = Math.max(n, m);

        const C: number[][] = [];
        for (let i = 0; i < size; i++) {
            const row: number[] = [];
            for (let j = 0; j < size; j++) {
                if (i >= n || j >= m) {
                    row.push(0);
                    continue;
                }
                const dx = fA[i].cx - fB[j].cx;
                const dy = fA[i].cy - fB[j].cy;
                row.push(Math.sqrt(dx * dx + dy * dy) + 0.5 * Math.abs(fA[i].sr - fB[j].sr));
            }
            C.push(row);
        }

        const INF = Infinity;
        const u = new Array(size + 1).fill(0);
        const v = new Array(size + 1).fill(0);
        const p = new Array(size + 1).fill(0);
        const way = new Array(size + 1).fill(0);

        for (let i = 1; i <= size; i++) {
            p[0] = i;
            let j0 = 0;
            const minv = new Array(size + 1).fill(INF);
            const used = new Array(size + 1).fill(false);
            do {
                used[j0] = true;
                const i0 = p[j0];
                let delta = INF;
                let j1 = -1;
                for (let j = 1; j <= size; j++) {
                    if (used[j]) {
                        continue;
                    }
                    const val = C[i0 - 1][j - 1] - u[i0] - v[j];
                    if (val < minv[j]) {
                        minv[j] = val;
                        way[j] = j0;
                    }
                    if (minv[j] < delta) {
                        delta = minv[j];
                        j1 = j;
                    }
                }
                for (let j = 0; j <= size; j++) {
                    if (used[j]) {
                        u[p[j]] += delta;
                        v[j] -= delta;
                    } else {
                        minv[j] -= delta;
                    }
                }
                j0 = j1;
            } while (p[j0] !== 0);
            do {
                p[j0] = p[way[j0]];
                j0 = way[j0];
            } while (j0);
        }

        const ans = new Array(size);
        for (let j = 1; j <= size; j++) {
            if (p[j] > 0) {
                ans[p[j] - 1] = j - 1;
            }
        }

        const pairs: MorphPair[] = [];
        const usedB: Record<number, boolean> = {};
        for (let i = 0; i < n; i++) {
            const j = ans[i];
            if (j < m) {
                pairs.push({ a: pathsA[i], b: pathsB[j] });
                usedB[j] = true;
            } else {
                pairs.push({ a: pathsA[i], b: null });
            }
        }
        for (let j = 0; j < m; j++) {
            if (!usedB[j]) {
                pairs.push({ a: null, b: pathsB[j] });
            }
        }
        return pairs;
    }

    function resolve(target: string | Element, label: string): Element {
        const el = typeof target === 'string' ? document.querySelector(target) : target;
        if (!el) {
            throw new Error('L5Morph.morph: no element matched ' + label + ' "' + target + '"');
        }
        return el;
    }

    function viewBoxOf(el: Element, fallback: Element): MorphViewBox {
        const own = el.getAttribute('viewBox');
        if (own) {
            return parseViewBox(own);
        }
        const owner = el.closest('svg') || fallback.closest('svg') || fallback;
        return parseViewBox(owner.getAttribute('viewBox'));
    }

    function morph(tl: MorphTimeline, options: MorphOptions): SVGPathElement[] {
        const from = resolve(options.from, 'from');
        const to = resolve(options.to, 'to');
        const stage = resolve(options.stage, 'stage');
        const at = options.at === undefined ? 0 : options.at;
        const duration = options.duration === undefined ? 1 : options.duration;
        const ease = options.ease === undefined ? 'power3.inOut' : options.ease;

        const vbA = viewBoxOf(from, stage);
        const vbB = viewBoxOf(to, stage);
        const pairs = pairPaths(getPaths(from), getPaths(to), vbA, vbB);

        stage.innerHTML = '';
        stage.setAttribute('viewBox', vbA.x + ' ' + vbA.y + ' ' + vbA.w + ' ' + vbA.h);

        const morphEls = pairs.map(function (pair) {
            const el = document.createElementNS(NS, 'path') as SVGPathElement;
            stage.appendChild(el);
            const startFill = options.fill || (pair.a ? pair.a.fill : (pair.b as MorphPath).fill);
            gsapGlobal().set(el, {
                attr: { d: pair.a ? pair.a.d : centerDotPath(pair.b as MorphPath, vbB, vbA) },
                fill: startFill,
                opacity: pair.a ? 1 : 0,
            });
            return el;
        });

        morphEls.forEach(function (el, i) {
            const pair = pairs[i];
            tl.to(
                el,
                {
                    morphSVG: pair.b ? pair.b.d : centerDotPath(pair.a as MorphPath, vbA, vbB),
                    fill: options.toFill || (pair.b ? pair.b.fill : (pair.a as MorphPath).fill),
                    opacity: pair.b ? 1 : 0,
                    duration: duration,
                    ease: ease,
                },
                at,
            );
        });

        const vb = { x: vbA.x, y: vbA.y, w: vbA.w, h: vbA.h };
        tl.to(
            vb,
            {
                x: vbB.x,
                y: vbB.y,
                w: vbB.w,
                h: vbB.h,
                duration: duration,
                ease: ease,
                onUpdate: function () {
                    stage.setAttribute('viewBox', vb.x + ' ' + vb.y + ' ' + vb.w + ' ' + vb.h);
                },
            },
            at,
        );

        return morphEls;
    }

    return { morph: morph };
}

export const MORPH_HELPER_SOURCE = `window.L5Morph={morph:(${createMorphHelpers.toString()})().morph};`;
